// Proton Dropdown (packages/components/components/dropdown/*): a fixed-position
// panel anchored to its trigger, portalled to <body>, with the Proton look
// (.dropdown / .dropdown-content in src/proton/components.css). Placement flips
// to the opposite side when the preferred one would leave the viewport.
import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
  type ButtonHTMLAttributes, type CSSProperties, type ReactNode, type RefObject,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

export type Placement = "bottom-start" | "bottom-end" | "top-start" | "top-end" | "right-start" | "left-start";

interface TriggerArgs {
  ref: RefObject<HTMLButtonElement>;
  open: boolean;
  toggle: () => void;
  close: () => void;
}

export interface DropdownProps {
  trigger: (a: TriggerArgs) => ReactNode;
  children: (close: () => void) => ReactNode;
  placement?: Placement;
  /** Extra classes on the .dropdown panel (e.g. "rounded-lg"). */
  className?: string;
  contentClassName?: string;
  /** CSS lengths; Proton defaults are 10em / 20em / 30em. */
  minWidth?: string;
  maxWidth?: string;
  maxHeight?: string;
  /** Gap between trigger and panel, px. */
  offset?: number;
  onOpenChange?: (open: boolean) => void;
}

const MARGIN = 8;

export function place(anchor: DOMRect, panel: { width: number; height: number }, placement: Placement, offset: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let [side, align] = placement.split("-") as ["bottom" | "top" | "right" | "left", "start" | "end"];
  if (side === "bottom" && anchor.bottom + offset + panel.height > vh - MARGIN && anchor.top - offset - panel.height > MARGIN) side = "top";
  else if (side === "top" && anchor.top - offset - panel.height < MARGIN && anchor.bottom + offset + panel.height < vh - MARGIN) side = "bottom";
  if (side === "right" && anchor.right + offset + panel.width > vw - MARGIN) side = "left";

  let top: number;
  let left: number;
  if (side === "bottom" || side === "top") {
    top = side === "bottom" ? anchor.bottom + offset : anchor.top - offset - panel.height;
    left = align === "start" ? anchor.left : anchor.right - panel.width;
  } else {
    left = side === "right" ? anchor.right + offset : anchor.left - offset - panel.width;
    top = anchor.top;
  }
  left = Math.min(Math.max(MARGIN, left), vw - panel.width - MARGIN);
  top = Math.min(Math.max(MARGIN, top), vh - panel.height - MARGIN);
  const origin = `${side === "top" ? "bottom" : "top"}-${align === "end" || side === "left" ? "right" : "left"}`;
  return { top, left, origin };
}

export function Dropdown({
  trigger, children, placement = "bottom-start", className, contentClassName,
  minWidth, maxWidth, maxHeight, offset = 4, onOpenChange,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; origin: string } | null>(null);

  const setOpenState = useCallback((v: boolean) => { setOpen(v); onOpenChange?.(v); if (!v) setPos(null); }, [onOpenChange]);
  const close = useCallback(() => setOpenState(false), [setOpenState]);
  const toggle = useCallback(() => setOpenState(!open), [open, setOpenState]);

  const reposition = useCallback(() => {
    const a = anchorRef.current?.getBoundingClientRect();
    const panel = panelRef.current;
    if (!a || !panel) return;
    // Layout size, not getBoundingClientRect: the open animation starts the
    // panel at scale(0.75), which would under-measure it by a quarter.
    setPos(place(a, { width: panel.offsetWidth, height: panel.offsetHeight }, placement, offset));
  }, [placement, offset]);

  useLayoutEffect(() => { if (open) reposition(); }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); close(); anchorRef.current?.focus(); } };
    const onResize = () => reposition();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, close, reposition]);

  const style = {
    top: pos?.top ?? -9999,
    left: pos?.left ?? -9999,
    visibility: pos ? "visible" : "hidden",
    ...(minWidth ? { "--min-width": minWidth } : {}),
    ...(maxWidth ? { "--custom-max-width": maxWidth } : {}),
    ...(maxHeight ? { "--custom-max-height": maxHeight } : {}),
  } as CSSProperties;

  return (
    <>
      {trigger({ ref: anchorRef, open, toggle, close })}
      {open && createPortal(
        <div ref={panelRef} className={clsx("dropdown", className)} style={style} data-origin={pos?.origin} role="dialog">
          <div className={clsx("dropdown-content", contentClassName)}>{children(close)}</div>
        </div>,
        document.body,
      )}
    </>
  );
}

export function DropdownMenu({ children, className }: { children: ReactNode; className?: string }) {
  return <ul role="menu" className={clsx("m-0 p-0", className)}>{children}</ul>;
}

export function DropdownMenuButton({
  children, className, isSelected, danger, icon, right, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { isSelected?: boolean; danger?: boolean; icon?: ReactNode; right?: ReactNode }) {
  return (
    <li className={clsx("dropdown-item", danger && "dropdown-item--delete")} role="none">
      <button type="button" role="menuitem" className={clsx("dropdown-item-button", isSelected && "dropdown-item--is-selected", className)} aria-checked={isSelected} {...rest}>
        {icon && <span className="dropdown-item-icon inline-flex">{icon}</span>}
        <span className="flex-1 text-ellipsis">{children}</span>
        {right}
      </button>
    </li>
  );
}

export function DropdownSeparator() {
  return <li role="separator" className="dropdown-item"><hr className="dropdown-item-hr" /></li>;
}

export function DropdownTitle({ children }: { children: ReactNode }) {
  return <li role="presentation" className="dropdown-item dropdown-item-title">{children}</li>;
}
